/**
 * LDS financial interpretation — read-only passthrough from Backend SSOT.
 */
import { resolveLdsBackendFinancialSsot } from "./ldsBackendFinancialSsot.js";

export const LDS_FINANCIAL_MONEY_TOLERANCE = 0.02;

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseAuditIntegrity(row) {
  const audit = row?.post_payment_checkout_audit ?? row?.postPaymentCheckoutAudit;
  if (!audit || typeof audit !== "object") return {};
  const integrity = audit.integrity;
  return integrity && typeof integrity === "object" ? integrity : {};
}

function parsePaymentIntegrity(row) {
  const pi = row?.payment_integrity;
  if (pi && typeof pi === "object") return pi;
  return {};
}

function truthyPaidFlag(raw) {
  return raw === true || raw === "true" || raw === 1 || raw === "1";
}

function pickPaymentIntegrityStatus(order) {
  const pi = parsePaymentIntegrity(order);
  const audit = parseAuditIntegrity(order);
  return (
    pi.payment_integrity_status ??
    pi.paymentIntegrityStatus ??
    audit.payment_integrity_status ??
    order?.payment_integrity_status ??
    null
  );
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

function emitDevTrace(order, state, backend) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[LDS_FINANCIAL_STATE_TRACE]", {
      order_id: String(order?.id ?? order?._id ?? ""),
      paid: truthyPaidFlag(order?.paid),
      sumpaid: toNumberOrNull(order?.sumpaid),
      resolvedTotal: state.totalAmount,
      confirmedPaid: state.paidAmount,
      remainingBalance: state.remainingBalance,
      isFullyPaid: state.isFullyPaid,
      isPartialPaid: state.isPartialPaid,
      totalSource: backend.totalSource,
      hasBackendBlock: backend.hasBackendBlock,
    });
  } catch {
    /* ignore */
  }
}

/**
 * @param {object|null|undefined} order
 * @param {{ totalAmountOverride?: number|null }} [options]
 */
export function resolveLdsFinancialState(order, options = {}) {
  const empty = {
    totalAmount: null,
    paidAmount: 0,
    remainingBalance: null,
    isFullyPaid: false,
    isPartialPaid: false,
    isUnpaid: true,
    shouldShowPaidBadge: false,
    shouldShowContinuePayment: false,
    shouldNavigateSuccess: false,
    shouldShowPartialPaymentState: false,
    paymentIntegrityStatus: null,
    extraPaymentsIls: null,
    debug: {
      paid: false,
      sumpaid: null,
      confirmedPaid: 0,
      resolvedTotal: null,
      sourceUsed: "empty_order",
      hasBackendBlock: false,
      rejectedCandidates: [],
    },
  };

  if (!order || typeof order !== "object") {
    return empty;
  }

  const backend = resolveLdsBackendFinancialSsot(order, {
    emitTrace: false,
    totalAmountOverride: options?.totalAmountOverride,
  });

  const tol = LDS_FINANCIAL_MONEY_TOLERANCE;
  const totalAmount = backend.total;
  const paidAmount = backend.paid ?? 0;
  const remainingBalance = backend.remaining;

  const isFullyPaid =
    (remainingBalance != null && remainingBalance <= tol) ||
    (totalAmount != null &&
      paidAmount + tol >= totalAmount &&
      totalAmount > tol);

  const isPartialPaid =
    !isFullyPaid && paidAmount > tol && (remainingBalance == null || remainingBalance > tol);

  const isUnpaid = paidAmount <= tol;

  const sourceUsed = [backend.totalSource, backend.paidSource, backend.remainingSource]
    .filter(Boolean)
    .join("|");

  const state = {
    totalAmount,
    paidAmount,
    remainingBalance,
    extraPaymentsIls: toNumberOrNull(order.extra_payments_ils ?? order.extraPaymentsIls),
    isFullyPaid,
    isPartialPaid,
    isUnpaid,
    shouldShowPaidBadge: isFullyPaid,
    shouldShowContinuePayment: remainingBalance != null && remainingBalance > tol,
    shouldNavigateSuccess: isFullyPaid,
    shouldShowPartialPaymentState: isPartialPaid,
    paymentIntegrityStatus: pickPaymentIntegrityStatus(order),
    debug: {
      paid: truthyPaidFlag(order.paid),
      sumpaid: toNumberOrNull(order.sumpaid),
      confirmedPaid: paidAmount,
      resolvedTotal: totalAmount,
      sourceUsed,
      hasBackendBlock: backend.hasBackendBlock,
      rejectedCandidates: backend.rejectedCandidates,
    },
  };

  emitDevTrace(order, state, backend);
  return state;
}

/** @deprecated Prefer resolveLdsFinancialState(order).isFullyPaid */
export function isLdsFinanciallyFullyPaid(order, options) {
  return resolveLdsFinancialState(order, options).isFullyPaid;
}
